import { twMerge } from 'tailwind-merge';
import { Disclosure as HeadlessDisclosure, Transition } from '@headlessui/react';
import { Fragment, createContext, useContext, useState, useEffect, cloneElement, Children, isValidElement } from 'react';

type Variant = 'default' | 'boxed';
type Mode = 'accordion' | 'independent';

const disclosureContext = createContext<{
    open: boolean;
    close: () => void;
    key: number | string;
}>({
    open: false,
    close: () => {},
    key: 0,
});

const groupContext = createContext<{
    selectedIndex: null | number | string;
    setSelectedIndex: (index: number | string) => void;
    variant: Variant;
    mode: Mode;
}>({
    selectedIndex: null,
    setSelectedIndex: () => {},
    variant: 'default',
    mode: 'accordion',
});

type DisclosureKey = number | string;

function Disclosure({
    children,
    className,
    itemKey = 0,
    ...props
}: ExtractProps<typeof HeadlessDisclosure> & {
    itemKey?: DisclosureKey;
}) {
    const group = useContext(groupContext);

    return (
        <HeadlessDisclosure
            as="div"
            {...(group.mode === 'accordion' ? { defaultOpen: group.selectedIndex === itemKey } : {})}
            className={twMerge([
                'py-4 first:-mt-4 last:-mb-4',
                '[&:not(:last-child)]:border-b [&:not(:last-child)]:border-slate-200/60 [&:not(:last-child)]:dark:border-darkmode-400',
                group.variant == 'boxed' && 'p-4 first:mt-0 last:mb-0 border border-slate-200/60 mt-3 dark:border-darkmode-400',
                className,
            ])}
            {...props}
        >
            {({ open, close }) => (
                <disclosureContext.Provider
                    value={{
                        open: open,
                        close: close,
                        key: itemKey as number,
                    }}
                >
                    <>
                        {typeof children === 'function'
                            ? children({
                                  open: open,
                                  close: close,
                              })
                            : children}
                    </>
                </disclosureContext.Provider>
            )}
        </HeadlessDisclosure>
    );
}

Disclosure.Group = <C extends React.ElementType = 'div'>({
    children,
    className,
    as,
    selectedIndex = 0,
    variant = 'default',
    mode = 'accordion',
    ...props
}: {
    as?: C;
    selectedIndex?: number;
    variant?: Variant;
    mode?: Mode;
} & React.PropsWithChildren &
    React.ComponentPropsWithoutRef<C>) => {
    const [active, setActive] = useState(selectedIndex);
    const Component = as || 'div';

    return (
        <groupContext.Provider
            value={{
                selectedIndex: active,
                setSelectedIndex: setActive as (index: number | string) => void,
                variant: variant,
                mode: mode,
            }}
        >
            <Component className={className} {...props}>
                {Array.isArray(children)
                    ? children.map((item, key) => {
                          return {
                              ...item,
                              props: {
                                  ...item.props,
                                  itemKey: key,
                                  key: key,
                              },
                          };
                      })
                    : children}
            </Component>
        </groupContext.Provider>
    );
};

Disclosure.Button = ({ children, className, ...props }: ExtractProps<typeof HeadlessDisclosure.Button>) => {
    const disclosure = useContext(disclosureContext);
    const group = useContext(groupContext);

    useEffect(() => {
        if (group.mode === 'accordion' && group.selectedIndex !== disclosure.key) {
            disclosure.close();
        }
    }, [group.mode, group.selectedIndex, disclosure.key, disclosure.close]);

    return (
        <HeadlessDisclosure.Button
            as="button"
            className={twMerge(['outline-none py-4 -my-4 font-medium w-full text-left dark:text-slate-400', disclosure.open && 'text-primary dark:text-slate-300', className])}
            onClick={() => {
                if (group.mode === 'accordion') {
                    group.setSelectedIndex(disclosure.key);
                }
            }}
            {...props}
        >
            {children}
        </HeadlessDisclosure.Button>
    );
};

Disclosure.Panel = ({ children, className, ...props }: ExtractProps<typeof HeadlessDisclosure.Panel>) => {
    return (
        <Transition
            as={Fragment}
            enter="overflow-hidden transition-all ease-out duration-300"
            enterFrom="max-h-0 opacity-0"
            enterTo="max-h-[4000px] opacity-100"
            leave="overflow-hidden transition-all ease-in duration-250"
            leaveFrom="max-h-[4000px] opacity-100"
            leaveTo="max-h-0 opacity-0"
        >
            <HeadlessDisclosure.Panel as="div" className={twMerge(['mt-3 text-slate-700 leading-relaxed dark:text-slate-400', className])} {...props}>
                {children}
            </HeadlessDisclosure.Panel>
        </Transition>
    );
};

export default Disclosure;
